import type { User } from "@client/hooks";
import { createAuthClient } from "better-auth/client";

/**
 * Better Auth client for React frontend
 * Handles authentication state and provides auth methods
 */
export const authClient = createAuthClient({
	/**
	 * Base URL should match your server's base URL
	 * In development, this will be the Vite dev server proxy
	 * In production, this will be your deployed domain
	 */
	baseURL:
		typeof window !== "undefined"
			? window.location.origin
			: "http://localhost:5173",

	/**
	 * Base path where Better Auth routes are mounted on the server
	 */
	basePath: "/api/auth",

	/**
	 * Fetch options for better error handling
	 */
	fetchOptions: {
		onError: (e) => {
			console.error("🚨 Auth Client: Fetch error:", e);
		},
	},
});

// Export the types for use in components
export type Session = typeof authClient.$Infer.Session;
export type { User };

/**
 * Helper function to get the current session
 * Useful for server-side rendering or manual session checks
 */
export const getSession = async () => {
	try {
		const session = await authClient.getSession();		
		return session;
	} catch (error) {
		console.error("🚨 Auth Client: Error getting session:", {
			error: error instanceof Error ? error.message : "Unknown error",
			stack: error instanceof Error ? error.stack : undefined,
			timestamp: new Date().toISOString(),
		});
		throw error;
	}
};

/**
 * Helper function to sign out
 * Can be used from anywhere in the app
 */
export const signOut = async () => {
	console.log("🔍 Auth Client: Starting sign out...");
	try {
		const result = await authClient.signOut();
		console.log("🔍 Auth Client: Sign out result:", {
			result,
			timestamp: new Date().toISOString(),
		});
		return result;
	} catch (error) {
		console.error("🚨 Auth Client: Error signing out:", {
			error: error instanceof Error ? error.message : "Unknown error",
			stack: error instanceof Error ? error.stack : undefined,
			timestamp: new Date().toISOString(),
		});
		throw error;
	}
};

/**
 * Helper function to sign in with Google
 */
export const signInWithGoogle = async (callbackURL = "/") => {
	console.log("🔍 Auth Client: Starting Google sign-in...", {
		callbackURL,
		currentUrl:
			typeof window !== "undefined" ? window.location.href : "unknown",
		timestamp: new Date().toISOString(),
	});

	try {
		const result = await authClient.signIn.social({
			provider: "google",
			callbackURL,
		});

		console.log("🔍 Auth Client: Google sign-in result:", {
			result,
			timestamp: new Date().toISOString(),
		});

		return {
			data: result,
			error: null,
		};
	} catch (error) {
		console.error("🚨 Auth Client: Error with Google sign-in:", {
			error: error instanceof Error ? error.message : "Unknown error",
			stack: error instanceof Error ? error.stack : undefined,
			timestamp: new Date().toISOString(),
		});

		// Create a structured error response
		const errorResponse = {
			data: null,
			error: {
				error: "Authentication error",
				message:
					error instanceof Error
						? error.message
						: "Unknown authentication error occurred",
				details: "Please try again or contact support if the problem persists",
				status:
					error instanceof Error && "status" in error
						? (error as any).status
						: 500,
				statusText:
					error instanceof Error && "statusText" in error
						? (error as any).statusText
						: "Internal Server Error",
			},
		};

		return errorResponse;
	}
};

export default authClient;
